import { AppSelect, AppTextarea } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { BadgeForAnoStatus } from '@inquiries/-components/BadgeForAnoStatus'
import { useContacts } from '@inquiries/-hooks/useContacts'
import { useMemo } from 'react'
import type { Contact } from '@/lib/apiClients/inquiry'
import type { SalesforceDealAno } from '@/lib/apiClients/salesforce'
import { useSalesforceDealAnos } from '@/routes/_authenticated/inquiries/-components/CreateDealDialog/hooks/useSalesforceDealAnos'
import type { InquiryEditFormValues } from '../../../hooks/useInquiryEditForm'

const ANO_PLACEHOLDERS = {
  LOADING: '読み込み中...',
  SELECT: 'ANOを選択',
  NO_SELECTABLE: '紐づけ可能なANOがありません',
  NOT_LINKED: 'ANOが紐づいていません',
} as const

type ProjectOverviewSectionProps = {
  /**
   * salesforceDealId が null（商談なしの引合）なら ANO 候補を、salesforceCustomerId が null なら取引先責任者を取得しない。
   * 商談なしの引合の salesforceCustomerId は引合の顧客
   */
  salesforceCustomerId: number | null
  salesforceDealId: number | null
  /** DB上の現在の salesforceProjectId（クリア後も再選択可能にするため保持） */
  initialSalesforceProjectId: number | null
  formValues: InquiryEditFormValues
  formErrors: Partial<Record<keyof InquiryEditFormValues, string>>
  onFieldChange: <K extends keyof InquiryEditFormValues>(key: K, value: InquiryEditFormValues[K]) => void
  /** ANO を選び直したとき（外したら null）。関係する値（SF 側の部門・主管部門・営業・SMG）の整合は hook がとる */
  onAnoChange: (ano: SalesforceDealAno | null) => void
  isInquiryCompleted: boolean
}

export function ProjectOverviewSection({
  salesforceCustomerId,
  salesforceDealId,
  initialSalesforceProjectId,
  formErrors,
  formValues,
  onFieldChange,
  onAnoChange,
  isInquiryCompleted,
}: ProjectOverviewSectionProps) {
  const { data: contactsData, isLoading: isLoadingContacts } = useContacts(salesforceCustomerId ?? undefined)
  const contactOptions = useMemo(
    () =>
      contactsData?.contacts.map((c: Contact) => ({
        value: String(c.id),
        label: c.name,
      })) ?? [],
    [contactsData],
  )
  const getContactState = () => {
    if (isLoadingContacts) return { disabled: true, placeholder: '読み込み中...' }
    if (contactOptions.length === 0)
      return { disabled: true, placeholder: '登録されている顧客に紐づく取引先責任者が存在しません' }
    return { disabled: false, placeholder: '取引先責任者を選択' }
  }
  const contactState = getContactState()

  // ANO選択
  const { data: anosData, isLoading: isLoadingAnos } = useSalesforceDealAnos(salesforceDealId ?? undefined)
  const anos = anosData?.anos ?? []
  const anoOptions = useMemo(
    () =>
      anos.map((a) => ({
        value: String(a.salesforce_project_id),
        label: a.name,
        disabled:
          !a.enabled &&
          a.salesforce_project_id !== formValues.salesforceProjectId &&
          a.salesforce_project_id !== initialSalesforceProjectId,
        rightItem: (
          <BadgeForAnoStatus
            inquiryStatus={a.inquiry_status}
            salesforceProjectId={a.salesforce_project_id}
            ano={a.ano}
            currentAnoProjectId={initialSalesforceProjectId}
          />
        ),
      })),
    [anos, formValues.salesforceProjectId, initialSalesforceProjectId],
  )
  const hasSelectableAno = anoOptions.some((opt) => !opt.disabled)
  const anoState: { disabled: boolean; placeholder: string } = (() => {
    if (isLoadingAnos) return { disabled: true, placeholder: ANO_PLACEHOLDERS.LOADING }
    if (anoOptions.length === 0) return { disabled: true, placeholder: ANO_PLACEHOLDERS.NO_SELECTABLE }
    if (isInquiryCompleted) return { disabled: true, placeholder: ANO_PLACEHOLDERS.NOT_LINKED }
    if (!hasSelectableAno) return { disabled: false, placeholder: ANO_PLACEHOLDERS.NO_SELECTABLE }
    return { disabled: false, placeholder: ANO_PLACEHOLDERS.SELECT }
  })()

  const handleAnoChange = (value: string) =>
    onAnoChange(anos.find((a) => String(a.salesforce_project_id) === value) ?? null)

  return (
    <section className='space-y-3'>
      <h3 className='text-base font-semibold'>案件概要</h3>
      <AppTextarea
        label='案件概要'
        value={formValues.overview}
        onChange={(e) => onFieldChange('overview', e.target.value)}
        rows={3}
        limitHeight='lg'
        error={formErrors.overview}
        disabled={isInquiryCompleted}
      />
      <AppTextarea
        label='アサイン要件'
        value={formValues.requirements}
        onChange={(e) => onFieldChange('requirements', e.target.value)}
        rows={4}
        limitHeight='lg'
        required
        error={formErrors.requirements}
        disabled={isInquiryCompleted}
      />
      <div>
        <AppSelect
          id='ano'
          label='ANO'
          value={formValues.salesforceProjectId != null ? String(formValues.salesforceProjectId) : ''}
          onValueChange={handleAnoChange}
          options={anoOptions}
          placeholder={anoState.placeholder}
          className='w-full'
          disabled={anoState.disabled}
          clearable={!anoState.disabled}
          onClear={() => handleAnoChange('')}
        />
        {isInquiryCompleted && <p className='text-muted-foreground text-sm mt-1'>確定済みのため変更できません</p>}
      </div>
      <AppSelect
        id='contact'
        label='取引先責任者'
        value={formValues.contactId != null ? String(formValues.contactId) : ''}
        onValueChange={(value) => onFieldChange('contactId', value ? Number(value) : null)}
        options={contactOptions}
        placeholder={contactState.placeholder}
        className='w-full'
        disabled={contactState.disabled || isInquiryCompleted}
        clearable={!contactState.disabled}
        onClear={() => onFieldChange('contactId', null)}
      />
    </section>
  )
}
